import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { diffLines } from 'diff';
import { useSelector } from 'react-redux';

import useApiCall from '../../hooks/useApiCall';
import { INDIGO, SLATE } from '../../constants/Colors';

const FILE_TABS = [
  { key: 'agent', label: 'Agent Config' },
  { key: 'system_instructions', label: 'System Instructions' },
  { key: 'handoff_channels', label: 'Handoff Channels' },
  { key: 'prompt_variables', label: 'Prompt Variables' },
];

// ── Diff renderer ─────────────────────────────────────────────────────────────

const LINE_SX = {
  base: {
    display: 'flex',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: '0.8rem',
    lineHeight: '20px',
    minHeight: 20,
  },
  added:   { bgcolor: '#f0fdf4' },
  removed: { bgcolor: '#fef2f2' },
  neutral: { bgcolor: '#ffffff' },
};

const GUTTER_SX = {
  base: {
    minWidth: 44,
    px: '8px',
    textAlign: 'right',
    userSelect: 'none',
    color: SLATE[400],
    fontSize: '0.75rem',
    lineHeight: '20px',
    flexShrink: 0,
    borderRight: `1px solid ${SLATE[200]}`,
  },
  added:   { bgcolor: '#dcfce7' },
  removed: { bgcolor: '#fee2e2' },
  neutral: { bgcolor: '#f8fafc' },
};

const MARKER_COLOR = { added: '#16a34a', removed: '#dc2626', neutral: 'transparent' };
const MARKER_CHAR  = { added: '+', removed: '−', neutral: ' ' };

function buildLines(oldStr, newStr) {
  const parts = diffLines(oldStr || '', newStr || '');
  const oldLines = [];
  const newLines = [];

  parts.forEach(part => {
    const lines = part.value.replace(/\n$/, '').split('\n');
    if (part.added) {
      lines.forEach(l => newLines.push({ text: l, type: 'added' }));
    } else if (part.removed) {
      lines.forEach(l => oldLines.push({ text: l, type: 'removed' }));
    } else {
      lines.forEach(l => {
        oldLines.push({ text: l, type: 'neutral' });
        newLines.push({ text: l, type: 'neutral' });
      });
    }
  });

  // Pad shorter side so rows align
  const max = Math.max(oldLines.length, newLines.length);
  while (oldLines.length < max) oldLines.push({ text: '', type: 'neutral' });
  while (newLines.length < max) newLines.push({ text: '', type: 'neutral' });

  return { oldLines, newLines };
}

function DiffLine({ lineNum, text, type }) {
  const variant = type === 'added' ? 'added' : type === 'removed' ? 'removed' : 'neutral';
  return (
    <Box sx={{ ...LINE_SX.base, ...LINE_SX[variant] }}>
      {/* Gutter */}
      <Box sx={{ ...GUTTER_SX.base, ...GUTTER_SX[variant] }}>
        {lineNum != null ? lineNum : ''}
      </Box>
      {/* Marker */}
      <Box
        sx={{
          minWidth: 20,
          px: '4px',
          color: MARKER_COLOR[variant],
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {MARKER_CHAR[variant]}
      </Box>
      {/* Content */}
      <Box
        component="pre"
        sx={{
          m: 0, px: '8px', flex: 1,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit',
          color: variant === 'added' ? '#15803d' : variant === 'removed' ? '#dc2626' : SLATE[800],
        }}
      >
        {text || ' '}
      </Box>
    </Box>
  );
}

function SplitDiffView({ oldValue, newValue }) {
  const { oldLines, newLines } = buildLines(oldValue, newValue);

  const hasChanges = oldLines.some(l => l.type !== 'neutral') ||
                     newLines.some(l => l.type !== 'neutral');

  if (!hasChanges) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <Typography sx={{ fontSize: '0.875rem', color: SLATE[400] }}>
          No changes between branches
        </Typography>
      </Box>
    );
  }

  let oldNum = 0;
  let newNum = 0;

  return (
    <Box sx={{ display: 'flex', width: '100%', overflow: 'auto' }}>
      {/* Old (main) */}
      <Box sx={{ flex: 1, minWidth: 0, borderRight: `2px solid ${SLATE[200]}` }}>
        <Box
          sx={{
            px: 2, py: '6px',
            bgcolor: SLATE[50],
            borderBottom: `1px solid ${SLATE[200]}`,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: SLATE[500],
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          }}
        >
          main
        </Box>
        {oldLines.map((line, i) => {
          if (line.type !== 'neutral' || line.text !== '') oldNum += line.type === 'neutral' ? 1 : 1;
          const num = line.text === '' && line.type === 'neutral' ? null : ++oldNum - 1;
          return <DiffLine key={i} lineNum={num} text={line.text} type={line.type} />;
        })}
      </Box>

      {/* New (current) */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            px: 2, py: '6px',
            bgcolor: SLATE[50],
            borderBottom: `1px solid ${SLATE[200]}`,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: SLATE[500],
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          }}
        >
          current
        </Box>
        {newLines.map((line, i) => {
          if (line.text === '' && line.type === 'neutral') {
            return <DiffLine key={i} lineNum={null} text={line.text} type={line.type} />;
          }
          newNum++;
          return <DiffLine key={i} lineNum={newNum} text={line.text} type={line.type} />;
        })}
      </Box>
    </Box>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export default function DiffViewerModal({ open, onClose, appId, agentId }) {
  const tokens = useSelector(s => s?.tokens.value);
  const { makeRequest } = useApiCall();

  const [activeTab, setActiveTab] = useState(0);
  const [diffs, setDiffs] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchDiffs = useCallback(async () => {
    if (!appId || !agentId) return;
    setLoading(true);
    setDiffs({});
    setErrors({});

    const results = await Promise.allSettled(
      FILE_TABS.map(tab =>
        makeRequest(`/v1/apps/${appId}/agents/${agentId}/git/diff?file_type=${tab.key}`, {
          method: 'GET',
          authTokens: tokens,
          ignore_error: true,
        })
      )
    );

    const newDiffs = {};
    const newErrors = {};
    results.forEach((result, idx) => {
      const key = FILE_TABS[idx].key;
      if (result.status === 'fulfilled' && result.value) {
        newDiffs[key] = result.value;
      } else {
        newErrors[key] = result.reason?.message || 'Failed to load diff';
      }
    });

    setDiffs(newDiffs);
    setErrors(newErrors);
    setLoading(false);
  }, [appId, agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      fetchDiffs();
    }
  }, [open, fetchDiffs]);

  const activeKey = FILE_TABS[activeTab]?.key;
  const activeDiff = diffs[activeKey];
  const activeError = errors[activeKey];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 0,
          borderBottom: `1px solid ${SLATE[200]}`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900] }}>
          View Changes
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: SLATE[400] }}>
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          px: 3,
          flexShrink: 0,
          borderBottom: `1px solid ${SLATE[200]}`,
          '& .MuiTab-root': {
            fontSize: '0.8125rem',
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 44,
            color: SLATE[500],
          },
          '& .Mui-selected': { color: INDIGO[600] },
          '& .MuiTabs-indicator': { bgcolor: INDIGO[600] },
        }}
      >
        {FILE_TABS.map(tab => (
          <Tab key={tab.key} label={tab.label} />
        ))}
      </Tabs>

      <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: '0.875rem', color: SLATE[500] }}>Loading diff…</Typography>
          </Box>
        ) : activeError ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
            <Typography sx={{ fontSize: '1.5rem' }}>
              {activeError.toLowerCase().includes('not found') ? '📄' : '⚠️'}
            </Typography>
            <Typography sx={{
              fontSize: '0.875rem',
              color: activeError.toLowerCase().includes('not found') ? SLATE[500] : '#dc2626',
            }}>
              {activeError}
            </Typography>
          </Box>
        ) : activeDiff ? (
          <SplitDiffView oldValue={activeDiff.old} newValue={activeDiff.new} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: '0.875rem', color: SLATE[400] }}>No data</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
